/** 包装换算：库存数量始终按最小单位 */

export function packRatioOf(item) {
  const r = Number(item && (item.pack_ratio != null ? item.pack_ratio : item.packRatio));
  return r > 1 ? r : 1;
}

export function packUnitOf(item) {
  return (item && (item.pack_unit_name || item.packUnitName)) || "件";
}

export function baseUnitOf(item) {
  return (item && (item.unit || item.unit_name || item.unitName || item.baseUnit)) || "最小单位";
}

export function hasPack(item) {
  return packRatioOf(item) > 1;
}

export function contentLabel(item) {
  return `${packRatioOf(item)}${baseUnitOf(item)}/${packUnitOf(item)}`;
}

function trimNum(n) {
  const x = Number(n);
  if (!Number.isFinite(x)) return "0";
  return String(Math.round(x * 1000) / 1000);
}

/** 最小单位库存 → 整件 + 散量 */
export function splitBaseQty(baseQty, item) {
  const qty = Number(baseQty) || 0;
  const ratio = packRatioOf(item);
  if (ratio <= 1) return { packs: qty, loose: 0 };
  const packs = Math.floor((qty + 1e-9) / ratio);
  let loose = Math.round((qty - packs * ratio) * 1000) / 1000;
  if (Math.abs(loose) < 1e-9) loose = 0;
  if (loose < 0) loose = 0;
  return { packs, loose };
}

/** 报表数量：3160袋 / 3160袋+10斤；没写规格按件 */
export function formatStockQty(baseQty, item) {
  const qty = Number(baseQty) || 0;
  const baseUnit = baseUnitOf(item);
  if (!hasPack(item)) {
    return `${trimNum(qty)}件`;
  }
  const { packs, loose } = splitBaseQty(qty, item);
  const packUnit = packUnitOf(item);
  if (loose) return `${packs}${packUnit}+${trimNum(loose)}${baseUnit}`;
  return `${packs}${packUnit}`;
}

export function formatStockUnit(item) {
  return hasPack(item) ? packUnitOf(item) : "件";
}

/** 包装数量 → 最小单位数量 */
export function toBaseQty(packQty, item) {
  const ratio = packRatioOf(item);
  const n = Number(packQty) || 0;
  return Math.round(n * ratio * 1000) / 1000;
}

export function toBaseQtyFromParts(packQty, looseQty, item) {
  const packs = Number(packQty) || 0;
  const loose = Number(looseQty) || 0;
  return Math.round((packs * packRatioOf(item) + loose) * 1000) / 1000;
}

export function round2(n) {
  const x = Number(n);
  if (!Number.isFinite(x)) return 0;
  return Math.round(x * 100) / 100;
}

/** 库内单价(最小单位) → 一件进货价 */
export function toPackPrice(unitPrice, item) {
  const ratio = packRatioOf(item);
  const p = Number(unitPrice);
  if (!Number.isFinite(p)) return undefined;
  return ratio > 1 ? round2(p * ratio) : round2(p);
}

/** 一件进货价 → 库内单价(最小单位) */
export function toUnitPrice(packPrice, item) {
  const ratio = packRatioOf(item);
  const p = Number(packPrice);
  if (!Number.isFinite(p)) return undefined;
  return ratio > 1 ? round2(p / ratio) : round2(p);
}

export function lineBaseQty(item) {
  return toBaseQtyFromParts(item && item.pack_qty, item && item.loose_qty, item);
}

export function lineUnitPrice(item) {
  const p = Number(item && item.display_price);
  if (!Number.isFinite(p)) return 0;
  if ((item.price_mode || "pack") === "pack" && hasPack(item)) {
    return toUnitPrice(p, item) || 0;
  }
  return round2(p);
}

export function lineAmountOf(item) {
  return round2(lineBaseQty(item) * lineUnitPrice(item));
}

export function togglePriceMode(item) {
  const unit = lineUnitPrice(item);
  const next = item.price_mode === "pack" ? "unit" : "pack";
  item.price_mode = next;
  if (next === "pack" && hasPack(item)) {
    item.display_price = toPackPrice(unit, item);
  } else {
    item.display_price = unit;
  }
  return item;
}

export function formulaText(item) {
  const pack = Number(item && item.pack_qty) || 0;
  const loose = Number(item && item.loose_qty) || 0;
  const ratio = packRatioOf(item);
  const packUnit = packUnitOf(item);
  const baseUnit = baseUnitOf(item);
  const mode = (item && item.price_mode) || "pack";
  const display = Number(item && item.display_price);
  const displayStr = Number.isFinite(display) ? String(display) : "0";
  const unitPrice = lineUnitPrice(item);
  const amount = lineAmountOf(item);
  const packed = hasPack(item);

  if (!packed) {
    const qty = pack || loose;
    return `${qty}${baseUnit} × ${displayStr}元/${baseUnit} = ${amount}`;
  }

  if (mode === "pack") {
    const parts = [];
    if (pack) parts.push(`${pack}${packUnit} × ${displayStr}元/${packUnit}`);
    if (loose) parts.push(`${loose}${baseUnit} × ${unitPrice}元/${baseUnit}`);
    if (!parts.length) parts.push(`0${packUnit} × ${displayStr}元/${packUnit}`);
    return `${parts.join(" + ")} = ${amount}`;
  }

  const parts = [];
  if (pack) parts.push(`${pack}${packUnit} × ${ratio}${baseUnit}`);
  if (loose) parts.push(`${loose}${baseUnit}`);
  const qtyText = parts.length ? parts.join(" + ") : `0${baseUnit}`;
  return `${qtyText} × ${displayStr}元/${baseUnit} = ${amount}`;
}

/** 批发/采购开单默认：按件、件价 */
export function initWholesaleLine(src, unitPrice, extras = {}) {
  const packed = hasPack(src);
  const price = Number(unitPrice);
  const mode = extras.price_mode || (packed ? "pack" : "unit");
  let display;
  if (extras.display_price != null && extras.display_price !== "") {
    display = Number(extras.display_price);
  } else if (Number.isFinite(price)) {
    display = mode === "pack" && packed ? toPackPrice(price, src) : round2(price);
  }
  return {
    pack_ratio: packRatioOf(src),
    pack_unit_name: packUnitOf(src),
    unit: baseUnitOf(src),
    pack_qty: extras.pack_qty != null ? extras.pack_qty : 1,
    loose_qty: extras.loose_qty != null ? extras.loose_qty : undefined,
    price_mode: mode,
    display_price: display,
  };
}

/** 退货默认：按最小单位 */
export function initReturnLine(src, unitPrice, extras = {}) {
  const price = Number(unitPrice);
  let display;
  if (extras.display_price != null && extras.display_price !== "") {
    display = Number(extras.display_price);
  } else if (Number.isFinite(price)) {
    display = round2(price);
  }
  return {
    pack_ratio: packRatioOf(src),
    pack_unit_name: packUnitOf(src),
    unit: baseUnitOf(src),
    pack_qty: extras.pack_qty != null ? extras.pack_qty : undefined,
    loose_qty: extras.loose_qty != null ? extras.loose_qty : extras.base_qty != null ? extras.base_qty : 1,
    price_mode: extras.price_mode || "unit",
    display_price: display,
  };
}

/** 列表/详情数量展示，与 formatStockQty 相同 */
export function formatQtyWithUnit(baseQty, item) {
  return formatStockQty(baseQty, item);
}

/** 多条数量按单位合并，如 82袋 + 10斤 */
export function joinPackQtyDisplay(parts) {
  const list = Array.isArray(parts) ? parts : [];
  if (!list.length) return "0";
  const byUnit = {};
  list.forEach((p) => {
    if (!p) return;
    const qty = Number(p.qty != null ? p.qty : pickQty(p)) || 0;
    if (hasPack(p)) {
      const { packs, loose } = splitBaseQty(qty, p);
      const packUnit = packUnitOf(p);
      byUnit[packUnit] = (byUnit[packUnit] || 0) + packs;
      if (loose) {
        const baseUnit = baseUnitOf(p);
        byUnit[baseUnit] = (byUnit[baseUnit] || 0) + loose;
      }
    } else {
      const unit = p.unit_name || p.unit || "件";
      byUnit[unit] = (byUnit[unit] || 0) + qty;
    }
  });
  const text = Object.keys(byUnit)
    .filter((k) => byUnit[k])
    .map((k) => `${trimNum(byUnit[k])}${k}`)
    .join(" + ");
  return text || "0";
}

function pickQty(g, qtyKey) {
  if (qtyKey && g && g[qtyKey] != null && g[qtyKey] !== "") return g[qtyKey];
  if (!g) return 0;
  const preferred = ["sales_quantity", "purchase_quantity", "return_quantity", "quantity"];
  for (let i = 0; i < preferred.length; i++) {
    const k = preferred[i];
    if (g[k] != null && g[k] !== "") return g[k];
  }
  const found = Object.keys(g).find((x) => /_quantity$/.test(x));
  return found ? g[found] : 0;
}

function pickPrice(g, priceKey) {
  if (priceKey && g && g[priceKey] != null && g[priceKey] !== "") return g[priceKey];
  if (!g) return undefined;
  const preferred = ["sales_price", "purchase_price", "return_price", "price"];
  for (let i = 0; i < preferred.length; i++) {
    const k = preferred[i];
    if (g[k] != null && g[k] !== "") return g[k];
  }
  const found = Object.keys(g).find((x) => /_price$/.test(x) && x !== "display_price");
  return found ? g[found] : undefined;
}

/** 规格与产品资料一致：只用产品 spec，不用含量文案顶替 */
export function displaySpec(item) {
  return String((item && (item.goods_spec || item.spec)) || "").trim();
}

/** 一行货品：品名 / 规格 / 数量×单价 / 金额 */
export function formatCompactLine(g, qtyKey, priceKey) {
  const qty = pickQty(g, qtyKey);
  const unitPrice = pickPrice(g, priceKey);
  const packPrice = toPackPrice(unitPrice, g);
  const qtyText = formatStockQty(qty, g);
  const spec = displaySpec(g);
  let amount = g && (g.total_amount != null ? g.total_amount : g.amount);
  if (amount == null) {
    amount = round2((Number(qty) || 0) * (Number(unitPrice) || 0));
  }
  const priceText = packPrice != null ? String(packPrice) : "";
  return {
    name: (g && (g.goods_name || g.name)) || "-",
    spec,
    qtyPrice: priceText !== "" ? `${qtyText}×${priceText}` : qtyText,
    amount,
  };
}

/** 2026-09-05 / 2026-09-05 12:00:00 → 09-05 */
export function formatShortDate(val) {
  if (val == null || val === "") return "";
  const s = String(val);
  const m = s.match(/(\d{4})[-/](\d{1,2})[-/](\d{1,2})/);
  if (m) {
    const mm = m[2].length === 1 ? `0${m[2]}` : m[2];
    const dd = m[3].length === 1 ? `0${m[3]}` : m[3];
    return `${mm}-${dd}`;
  }
  if (s.length >= 10) return s.slice(5, 10);
  return s;
}
